import { testDiceData } from './test.js';
import { successesOf, throwOne } from './staged.js';
export const VEHICLE_COMBAT_LIMITS = { speed: 10000, distance: 100000, vehicles: 12, label: 40, modifier: 50, turns: 999, dice: 60, power: 100 };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v, d) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const int = (v, d) => (typeof v === 'number' && Number.isInteger(v) ? v : d);
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const numbers = (v) => {
    const out = {};
    if (isObj(v))
        for (const [k, n] of Object.entries(v))
            if (typeof n === 'number' && Number.isInteger(n) && Math.abs(n) <= VEHICLE_COMBAT_LIMITS.modifier)
                out[k] = n;
    return out;
};
const strings = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && KEY_RE.test(x)) : []);
function hitCode(v) {
    if (!isObj(v) || !Number.isInteger(v.power) || typeof v.level !== 'string' || !KEY_RE.test(v.level))
        return null;
    return { power: v.power, level: v.level, stun: v.stun === true };
}
const field = (v) => (typeof v === 'string' && KEY_RE.test(v) ? v : null);
/** The block off the system's rules (`combat.vehicle_combat`, or the block itself): the rules, or null without them. */
export function parseVehicleCombat(raw) {
    const v = isObj(raw) && isObj(raw.combat) && isObj(raw.combat.vehicle_combat) ? raw.combat.vehicle_combat : raw;
    if (!isObj(v) || !isObj(v.damage) || !Array.isArray(v.damage.levels))
        return null;
    const d = v.damage;
    const levels = [];
    for (const l of d.levels) {
        if (!isObj(l) || typeof l.level !== 'string' || !KEY_RE.test(l.level) || !Number.isInteger(l.boxes))
            return null;
        levels.push({ key: l.level, boxes: l.boxes, tn: int(l.tn, 0), initiative: int(l.initiative, 0), speedCut: int(l.speed_cut_percent, 0), inoperable: l.inoperable === true });
    }
    if (!levels.length)
        return null;
    levels.sort((a, b) => a.boxes - b.boxes);
    const o = isObj(d.ordinary) ? d.ordinary : {};
    const av = isObj(d.anti_vehicle) ? d.anti_vehicle : {};
    const rs = isObj(d.resist) ? d.resist : {};
    const rg = isObj(d.rigger) ? d.rigger : {};
    const hits = {};
    // The rigger's damage is keyed by the vehicle level it is felt at: the data's `serious` and `destroyed` (the top level).
    for (const [k, c] of Object.entries(rg)) {
        const code = hitCode(c);
        if (!code)
            continue;
        const at = k === 'destroyed' ? levels[levels.length - 1].key : k;
        hits[at] = code;
    }
    const ds = isObj(d.dump_shock) ? d.dump_shock : {};
    const dis = isObj(ds.disoriented) ? ds.disoriented : {};
    const dump = hitCode(ds) ?? { power: 0, level: levels[0].key, stun: true };
    const c = isObj(v.crashes) ? v.crashes : {};
    const ct = isObj(c.crash_test) ? c.crash_test : {};
    const cm = isObj(ct.modifiers) ? ct.modifiers : {};
    const speed = Array.isArray(cm.speed_vs_reaction) ? cm.speed_vs_reaction.filter(isObj).map((x) => ({ under: Number.isInteger(x.under) ? x.under : null, add: int(x.add, 0) })) : [];
    const impactLevels = Array.isArray(c.impact_levels) ? c.impact_levels.filter(isObj).filter((x) => typeof x.level === 'string').map((x) => ({ level: x.level, max: Number.isInteger(x.max) ? x.max : null })) : [];
    const pass = isObj(c.passengers) ? c.passengers : {};
    const g = isObj(v.gunnery) ? v.gunnery : {};
    const gm = isObj(g.manual) ? g.manual : {};
    const gs = isObj(g.sensor) ? g.sensor : {};
    const ch = isObj(v.chases) ? v.chases : {};
    const ms = isObj(ch.maneuver_score) ? ch.maneuver_score : {};
    const rel = isObj(ch.relocating) ? ch.relocating : {};
    const ini = isObj(ch.initiative) ? ch.initiative : {};
    const fi = isObj(v.initiative) ? v.initiative : null;
    const rigged = fi && isObj(fi.rigged) ? { bonus: field(fi.rigged.bonus), dice: field(fi.rigged.dice) } : null;
    const physical = fi && isObj(fi.physical) ? { bonus: field(fi.physical.bonus), dice: field(fi.physical.dice) } : null;
    return {
        levels,
        ordinary: { powerDivisor: Math.max(1, num(o.power_divisor, 1)), levelShift: int(o.level_shift, 0), lightDoesNothing: o.light_does_nothing === true },
        antiVehicle: { armorDivisor: Math.max(1, num(av.armor_divisor, 1)), powerDivisor: Math.max(1, num(av.power_divisor, 1)), levelShift: int(av.level_shift, 0) },
        resist: { stageEvery: Math.max(1, int(rs.stage_every, 2)), pool: field(rs.pool) },
        crashWhen: strings(d.crash_test_when),
        rigger: { hits, resist: field(rg.resist) ?? 'willpower' },
        dumpShock: { ...dump, resist: field(ds.resist) ?? 'willpower', tnAdd: int(dis.tn_add, 0), turns: int(dis.turns, 0) },
        impactLevels,
        restraintStageDown: int(pass.restraint_stage_down, 0),
        crash: { terrain: numbers(cm.terrain), speed, when: strings(ct.when) },
        gunnery: { manual: numbers(gm.modifiers), terrain: numbers(isObj(gm.modifiers) ? gm.modifiers.terrain : null), sizes: numbers(gm.target_size), sensor: numbers(gs.modifiers) },
        maneuver: { vehicles: numbers(ms.vehicle_points), terrains: numbers(ms.terrain_points), speedDivisor: Math.max(1, int(ms.speed_points_divisor, 10)) },
        givesUpTurns: Math.max(1, int(rel.npc_gives_up_turns, 5)),
        passengersAfterDriver: ini.passengers_first_pass_after_driver === true,
        initiative: rigged?.bonus && rigged.dice && physical?.bonus && physical.dice ? { rigged: { bonus: rigged.bonus, dice: rigged.dice }, physical: { bonus: physical.bonus, dice: physical.dice } } : null,
    };
}
export const NO_VEHICLE_COMBAT = "This table's game system has no vehicle combat, so the table cannot work it out.";
const words = (s) => s.replace(/_/g, ' ');
const levelIndex = (r, key) => r.levels.findIndex((l) => l.key === key);
// ── The vehicle's monitor ─────────────────────────────────────────────────
/** The level the vehicle's monitor is at with this many boxes filled, or null below the first. */
export function vehicleLevel(r, filled) {
    let at = null;
    for (const l of r.levels)
        if (filled >= l.boxes)
            at = l;
    return at;
}
/** What the vehicle's damage costs it now: the TN and initiative its level adds, its Speed cut, whether it still runs. */
export function vehicleCondition(r, filled, speed) {
    const l = vehicleLevel(r, filled);
    return { level: l?.key ?? null, tn: l?.tn ?? 0, initiative: l?.initiative ?? 0, speed: speed === null ? null : Math.floor((speed * (100 - (l?.speedCut ?? 0))) / 100), inoperable: l?.inoperable ?? false };
}
/**
 * A weapon's code against a vehicle's Armor: an ordinary weapon's Power divided and its level shifted (a level shifted
 * below the lowest does nothing), stopped by hardened armour at or above that Power, else the Armor off it; an
 * anti-vehicle weapon against the Armor divided, its Power and level as the data says.
 */
export function againstVehicle(r, code, armor, antiVehicle) {
    const at = levelIndex(r, code.level);
    if (at < 0)
        return 'That damage level is not one of the vehicle monitor\'s.';
    const rule = antiVehicle ? r.antiVehicle : r.ordinary;
    const power = Math.floor(code.power / rule.powerDivisor);
    const shifted = at + rule.levelShift;
    const level = shifted >= 0 ? r.levels[Math.min(shifted, r.levels.length - 1)].key : null;
    const against = antiVehicle ? Math.floor(armor / r.antiVehicle.armorDivisor) : armor;
    const note = antiVehicle ? `anti-vehicle: Power ${power} against Armor ${against}` : `Power ${code.power} → ${power}, ${words(code.level)} → ${level ? words(level) : 'nothing'}`;
    if (level === null || (!antiVehicle && r.ordinary.lightDoesNothing && at === 0 && rule.levelShift < 0))
        return { power, level: null, tn: 0, none: `A ${words(code.level)} wound does nothing to a vehicle.`, note };
    if (power <= against)
        return { power, level, tn: 0, none: `The vehicle's armour (${against}) stops Power ${power}.`, note };
    return { power, level, tn: power - against, none: null, note };
}
/** The vehicle's resistance dice: its Body and the driver's pool dice, at most the driving skill. */
export const vehicleResistDice = (body, pool, drivingSkill) => Math.max(0, body) + Math.max(0, Math.min(pool, drivingSkill));
/** Resist a code with `dice` against its Power (raised by `terms`): every `stageEvery` successes take a level off. */
export function resistCode(r, d, code, dice, label, rng, terms = []) {
    const at = levelIndex(r, code.level);
    if (at < 0)
        return 'That damage level is not one of the monitor\'s.';
    if (dice > VEHICLE_COMBAT_LIMITS.dice)
        return 'Those dice cannot be thrown.';
    const thrown = throwOne(d, { label, rating: dice, spent: null, dice, target: Math.max(1, code.power), base: 'Power', terms }, rng);
    if (typeof thrown === 'string')
        return thrown;
    const successes = successesOf(testDiceData(d), thrown);
    const left = at - Math.floor(successes / r.resist.stageEvery);
    const level = left >= 0 ? r.levels[left].key : null;
    return { thrown, successes, level, boxes: level ? r.levels[left].boxes : 0 };
}
/** After damage: `before` and `after` boxes filled, `taken` by this one attack. A rigger feels the highest level crossed. */
export function vehicleAftermath(r, before, after, taken, rigged) {
    const now = vehicleLevel(r, after);
    const top = r.levels[r.levels.length - 1];
    const crash = [];
    // The data names its causes: `<level>_in_one_attack` (that level's boxes taken at once) and `destroyed` (the top level reached).
    for (const why of r.crashWhen) {
        const once = /^(.+)_in_one_attack$/.exec(why);
        const l = once ? r.levels.find((x) => x.key === once[1]) : undefined;
        if (l && taken >= l.boxes)
            crash.push(why);
        else if (why === 'destroyed' && after >= top.boxes && before < top.boxes)
            crash.push(why);
    }
    let rigger = null;
    if (rigged) {
        for (const l of r.levels)
            if (after >= l.boxes && before < l.boxes && r.rigger.hits[l.key])
                rigger = r.rigger.hits[l.key];
    }
    return { crash, rigger, level: now?.key ?? null, inoperable: now?.inoperable ?? false };
}
/** The Crash Test's TN terms (against the Handling): the terrain, the speed against the driver's Reaction, damage and wounds. */
export function crashTerms(r, a) {
    const out = [];
    if (a.terrain !== undefined && r.crash.terrain[a.terrain])
        out.push({ label: `${words(a.terrain)} terrain`, value: r.crash.terrain[a.terrain] });
    const over = a.speed - a.reaction;
    const row = r.crash.speed.find((s) => s.under === null || over < s.under);
    if (row?.add)
        out.push({ label: 'speed against Reaction', value: row.add });
    if (a.damage)
        out.push({ label: 'vehicle damaged', value: a.damage });
    if (a.wound)
        out.push({ label: 'wounded', value: a.wound });
    return out;
}
/** An impact at a speed: Power the speed ÷ 10 up, its level by the speed's band. */
export function impactOf(r, speed) {
    const band = r.impactLevels.find((b) => b.max === null || speed <= b.max);
    if (!band || levelIndex(r, band.level) < 0)
        return 'The system names no impact for that speed.';
    return { power: Math.ceil(Math.max(0, speed) / 10), level: band.level, stun: false };
}
/** A ram: both resist the speed difference's impact; the rammer's Power less its Body times the Ramming Test's successes. */
export function rammingOf(r, a) {
    const target = impactOf(r, Math.abs(a.speedDifference));
    if (typeof target === 'string')
        return target;
    const less = target.power - a.rammerBody * a.successes;
    return { target, rammer: less > 0 ? { ...target, power: less } : null };
}
/** A passenger's code: the Power the vehicle faced, at the level it took, a level down when restrained (none below Light). */
export function passengerCode(r, faced, took, restrained) {
    if (took === null)
        return null;
    const at = levelIndex(r, took) - (restrained ? r.restraintStageDown : 0);
    return at >= 0 ? { power: faced.power, level: r.levels[at].key, stun: false } : null;
}
// ── Gunnery ───────────────────────────────────────────────────────────────
/** Gunnery's dice: manual, the skill; sensor-enhanced, the skill and half the vehicle's Sensor (rounded down). */
export const gunneryDice = (mode, gunnery, sensor) => (mode === 'sensor' ? gunnery + Math.floor(sensor / 2) : gunnery);
/**
 * Gunnery's TN terms: each situation ticked (a key of the data's number-valued modifiers for the mode), the terrain,
 * the target's size (manual), the vehicle's damage and the gunner's wounds. Unknown keys are refused.
 */
export function gunneryTerms(r, mode, a) {
    const table = mode === 'manual' ? r.gunnery.manual : r.gunnery.sensor;
    const out = [];
    for (const k of a.picks) {
        if (table[k] === undefined)
            return 'That is not one of this game system\'s gunnery situations.';
        if (table[k])
            out.push({ label: words(k).slice(0, VEHICLE_COMBAT_LIMITS.label), value: table[k] });
    }
    if (mode === 'manual' && a.terrain !== undefined && r.gunnery.terrain[a.terrain])
        out.push({ label: `${words(a.terrain)} terrain`, value: r.gunnery.terrain[a.terrain] });
    if (mode === 'manual' && a.size !== undefined) {
        if (r.gunnery.sizes[a.size] === undefined)
            return 'That is not one of this game system\'s target sizes.';
        if (r.gunnery.sizes[a.size])
            out.push({ label: 'target size', value: r.gunnery.sizes[a.size] });
    }
    if (a.damage)
        out.push({ label: 'own vehicle damaged', value: a.damage });
    if (a.wound)
        out.push({ label: 'wounded', value: a.wound });
    return out;
}
// ── The Maneuver Score and the chase ──────────────────────────────────────
/** A vehicle's Maneuver Score: its class's points, the terrain's, its speed ÷ the divisor (down) and the Driver Points. */
export function maneuverScore(r, a) {
    const vp = a.vehicleClass === null ? 0 : r.maneuver.vehicles[a.vehicleClass];
    if (vp === undefined)
        return 'That is not one of this game system\'s vehicle classes.';
    const tp = r.maneuver.terrains[a.terrain];
    if (tp === undefined)
        return 'That is not one of this game system\'s terrains.';
    const parts = [
        { label: 'vehicle', value: vp }, { label: `${words(a.terrain)} terrain`, value: tp },
        { label: 'speed', value: Math.floor(Math.max(0, a.speed) / r.maneuver.speedDivisor) }, { label: 'driver', value: a.driverPoints },
    ];
    return { total: parts.reduce((n, p) => n + p.value, 0), parts };
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
/** A chase off the wire or the store: exact shape or null. Untrusted input, always. */
export function parseChase(v) {
    if (!isObj(v) || Object.keys(v).some((k) => k !== 'turn' && k !== 'vehicles') || !isInt(v.turn, 0, VEHICLE_COMBAT_LIMITS.turns) || !Array.isArray(v.vehicles))
        return null;
    if (!v.vehicles.length || v.vehicles.length > VEHICLE_COMBAT_LIMITS.vehicles)
        return null;
    const out = [];
    for (const x of v.vehicles) {
        if (!isObj(x) || Object.keys(x).some((k) => !['token', 'speed', 'terrain', 'vehicleClass', 'driverPoints', 'at', 'hid', 'escaped'].includes(k)))
            return null;
        if (!UUID_RE.test(String(x.token)) || out.some((o) => o.token === x.token) || !isInt(x.speed, 0, VEHICLE_COMBAT_LIMITS.speed) || typeof x.terrain !== 'string' || !KEY_RE.test(x.terrain))
            return null;
        if (!(x.vehicleClass === null || (typeof x.vehicleClass === 'string' && x.vehicleClass.length <= VEHICLE_COMBAT_LIMITS.label)))
            return null;
        if (!(x.driverPoints === null || isInt(x.driverPoints, 0, VEHICLE_COMBAT_LIMITS.dice)) || !isInt(x.at, -VEHICLE_COMBAT_LIMITS.distance, VEHICLE_COMBAT_LIMITS.distance))
            return null;
        let hid;
        if (x.hid !== undefined) {
            const h = x.hid;
            if (!isObj(h) || Object.keys(h).some((k) => k !== 'successes' && k !== 'sought') || !isInt(h.successes, 1, VEHICLE_COMBAT_LIMITS.dice) || !isInt(h.sought, 0, VEHICLE_COMBAT_LIMITS.turns))
                return null;
            hid = { successes: h.successes, sought: h.sought };
        }
        if (x.escaped !== undefined && x.escaped !== true)
            return null;
        out.push({ token: String(x.token), speed: x.speed, terrain: x.terrain, vehicleClass: x.vehicleClass, driverPoints: x.driverPoints, at: x.at, ...(hid ? { hid } : {}), ...(x.escaped ? { escaped: true } : {}) });
    }
    return { turn: v.turn, vehicles: out };
}
/** The distance between two of the chase's vehicles, in metres. */
export const chaseDistance = (a, b) => Math.abs(a.at - b.at);
/** Each vehicle's score this turn (null until its Driver Points are in), in the chase's order. */
export function chaseScores(r, c) {
    return c.vehicles.map((v) => {
        if (v.driverPoints === null)
            return { token: v.token, score: null };
        const s = maneuverScore(r, { vehicleClass: v.vehicleClass, terrain: v.terrain, speed: v.speed, driverPoints: v.driverPoints });
        return typeof s === 'string' ? { token: v.token, score: null, refusal: s } : { token: v.token, score: s.total };
    });
}
/**
 * The end of a chase turn: each vehicle still in it moves on by its speed (so distances change by the speeds'
 * difference), the Driver Points are cleared for the next turn, and a vehicle that hid is sought one turn more — after
 * the data's turns of seeking it has got away.
 */
export function chaseTurnEnd(r, c) {
    const escaped = [];
    const vehicles = c.vehicles.map((v) => {
        if (v.escaped)
            return { ...v };
        const next = { ...v, at: Math.max(-VEHICLE_COMBAT_LIMITS.distance, Math.min(VEHICLE_COMBAT_LIMITS.distance, v.at + v.speed)), driverPoints: null };
        if (v.hid) {
            next.hid = { ...v.hid, sought: v.hid.sought + 1 };
            if (next.hid.sought >= r.givesUpTurns) {
                next.escaped = true;
                escaped.push(v.token);
            }
        }
        return next;
    });
    return { chase: { turn: c.turn + 1, vehicles }, escaped };
}
// ── The rigger's initiative ───────────────────────────────────────────────
const sheetOf = (sheet, f) => {
    const w = f.split('_');
    const n = sheet?.find((x) => x.words.length === w.length && w.every((y) => x.words.includes(y)));
    return n && Number.isInteger(n.value) ? n.value : null;
};
/** The initiative a token's sheet gives jacked in (`rigged`) or out (`physical`): its bonus and dice, or null when unread. */
export function riggingInitiative(r, sheet, jackedIn) {
    if (!r.initiative)
        return null;
    const f = jackedIn ? r.initiative.rigged : r.initiative.physical;
    const bonus = sheetOf(sheet, f.bonus);
    const dice = sheetOf(sheet, f.dice);
    return bonus === null || dice === null || dice < 1 ? null : { bonus, dice };
}
/**
 * Passengers after their driver in the first pass: for each passenger scoring at or above the driver, the change to its
 * score that puts it one below. Nothing when the system does not hold them back.
 */
export function passengerHolds(r, driverScore, passengers) {
    if (!r.passengersAfterDriver)
        return [];
    return passengers.filter((p) => p.score >= driverScore).map((p) => ({ id: p.id, change: driverScore - 1 - p.score }));
}
/** A viewer's copy of the chase (for the projection): only the vehicles `sees` keeps; none at all when it keeps none. */
export function chaseFor(chase, sees) {
    if (!chase)
        return null;
    const vehicles = chase.vehicles.filter((v) => sees(v.token));
    return vehicles.length ? { turn: chase.turn, vehicles: vehicles.map((v) => ({ ...v, ...(v.hid ? { hid: { ...v.hid } } : {}) })) } : null;
}
//# sourceMappingURL=vehicleCombat.js.map